import { tokens } from '@healthdesk/shared-utils';
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * MSI Splash Page (alternate)
 * QueueDisplay-style split-flap splash screen for:
 * - MSI³
 * - Medical SuperIntelligence
 * - rotating sub-brands:
 *   CHARTMIND / MASTERCHART / SAVOIR-CARE
 *
 * Notes:
 * - Uses uppercase-only flap characters to stay true to split-flap constraints.
 * - Displays MSI³ as a static brand lockup, while animated rows stay within supported chars.
 * - Primary brand blue is #1B4584.
 */

const CHARS = " 0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ-:".split("");

/** Hex for shadeHex(), inline CSS, and DOM styles (not MUI palette paths). */
const BRAND_BLUE = tokens.colors.primary.light;
const LIGHT_BLUE = tokens.colors.secondary.light;
const BRAND_RED = tokens.colors.msiRed.light;
const BRAND_GREEN = tokens.colors.msiGreen.light;
const MSI_ALT = tokens.splash.msiAlt;

const ROW_DELAY = 900;
const CHAR_STAGGER = 70;
const HALF_FLIP_MS = 75;

const PRODUCTS = [
  {
    name: "CHARTMIND",
    accent: LIGHT_BLUE,
    descriptor: "CLINICAL INTELLIGENCE",
  },
  {
    name: "MASTERCHART",
    accent: BRAND_GREEN,
    descriptor: "DOCUMENTATION WORKFLOW",
  },
  {
    name: "SAVOIR-CARE",
    accent: BRAND_RED,
    descriptor: "CARE ORCHESTRATION",
  },
];

function clampIndex(index) {
  if (index < 0) return 0;
  if (index >= CHARS.length) return CHARS.length - 1;
  return index;
}

function getCharIndex(char) {
  const idx = CHARS.indexOf(char);
  return idx === -1 ? 0 : idx;
}

function normalizeText(text, length) {
  return (text || "")
    .toUpperCase()
    .slice(0, length)
    .padEnd(length, " ");
}

function shadeHex(hex, percent) {
  const raw = hex.replace("#", "");
  const num = parseInt(raw, 16);
  const amt = Math.round(2.55 * percent);
  const r = Math.max(0, Math.min(255, (num >> 16) + amt));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amt));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amt));
  return `#${(0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function useFlipAudio(enabled) {
  const audioContextRef = useRef(null);

  const ensureAudio = () => {
    if (!enabled) return null;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;

    if (!audioContextRef.current) {
      audioContextRef.current = new Ctx();
    }

    if (audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume();
    }

    return audioContextRef.current;
  };

  const playFlip = () => {
    if (!enabled) return;
    const ctx = ensureAudio();
    if (!ctx) return;

    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = "square";
    osc.frequency.setValueAtTime(780, now);
    osc.frequency.exponentialRampToValueAtTime(260, now + 0.03);

    filter.type = "lowpass";
    filter.frequency.setValueAtTime(1700, now);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.045, now + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.05);

    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.02), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * 0.2;
    }

    const noise = ctx.createBufferSource();
    const noiseGain = ctx.createGain();
    const noiseFilter = ctx.createBiquadFilter();

    noise.buffer = buffer;
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.setValueAtTime(1350, now);

    noiseGain.gain.setValueAtTime(0.0001, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.012, now + 0.003);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);

    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(ctx.destination);

    noise.start(now);
    noise.stop(now + 0.02);
  };

  return { ensureAudio, playFlip };
}

function FlipChar({
  value,
  nextValue,
  isFlipping,
  width = 40,
  height = 60,
  bgColor = BRAND_BLUE,
  textColor = tokens.colors.bgCard.light,
  borderColor = "rgba(27, 69, 132, 0.3)",
}) {
  const topBg = shadeHex(bgColor, 6);
  const bottomBg = shadeHex(bgColor, -4);

  return (
    <div
      className="msi-flip-char"
      style={{
        width,
        height,
        borderColor,
      }}
      aria-hidden="true"
    >
      <div
        className="msi-flip-top"
        style={{
          background: topBg,
          color: textColor,
        }}
      >
        <span>{value === " " ? "\u00A0" : value}</span>
      </div>

      <div
        className="msi-flip-bottom"
        style={{
          background: bottomBg,
          color: textColor,
        }}
      >
        <span>{nextValue === " " ? "\u00A0" : nextValue}</span>
      </div>

      {isFlipping && (
        <>
          <div
            className="msi-flip-top-anim"
            style={{
              background: topBg,
              color: textColor,
              animationDuration: `${HALF_FLIP_MS}ms`,
            }}
          >
            <span>{value === " " ? "\u00A0" : value}</span>
          </div>

          <div
            className="msi-flip-bottom-anim"
            style={{
              background: bottomBg,
              color: textColor,
              animationDuration: `${HALF_FLIP_MS}ms`,
              animationDelay: `${HALF_FLIP_MS}ms`,
            }}
          >
            <span>{nextValue === " " ? "\u00A0" : nextValue}</span>
          </div>
        </>
      )}

      <div className="msi-flip-crease" />
    </div>
  );
}

function SplitFlapRow({
  text,
  width = 11,
  compact = false,
  bgColor = BRAND_BLUE,
  accentColor = LIGHT_BLUE,
  soundEnabled = true,
  className = "",
  /** Staggers this row’s first flip after the previous row (split-flap “row delay”). */
  rowIndex = 0,
}) {
  const displayText = useMemo(() => normalizeText(text, width), [text, width]);

  const [cells, setCells] = useState(() =>
    displayText.split("").map(() => ({
      current: " ",
      next: " ",
      isFlipping: false,
    }))
  );

  const previousTextRef = useRef(" ".repeat(width));
  const timersRef = useRef([]);
  const { ensureAudio, playFlip } = useFlipAudio(soundEnabled);

  useEffect(() => {
    const unlockAudio = () => ensureAudio();
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    return () => window.removeEventListener("pointerdown", unlockAudio);
  }, [ensureAudio]);

  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    const previous = normalizeText(previousTextRef.current, width);
    const next = displayText;

    const nextCells = previous.split("").map((char) => ({
      current: char,
      next: char,
      isFlipping: false,
    }));
    setCells(nextCells);

    const animateChar = (cellIndex, targetChar) => {
      let currentChar = previous[cellIndex];
      let currentIndex = clampIndex(getCharIndex(currentChar));
      const targetIndex = clampIndex(getCharIndex(targetChar));

      const step = () => {
        if (CHARS[currentIndex] === CHARS[targetIndex]) return;

        currentIndex = (currentIndex + 1) % CHARS.length;
        const nextChar = CHARS[currentIndex];

        setCells((prevCells) =>
          prevCells.map((cell, i) =>
            i === cellIndex
              ? {
                  current: currentChar,
                  next: nextChar,
                  isFlipping: true,
                }
              : cell
          )
        );

        playFlip();

        const settleTimer = window.setTimeout(() => {
          currentChar = nextChar;

          setCells((prevCells) =>
            prevCells.map((cell, i) =>
              i === cellIndex
                ? {
                    current: currentChar,
                    next: currentChar,
                    isFlipping: false,
                  }
                : cell
            )
          );

          if (currentChar !== targetChar) {
            const continueTimer = window.setTimeout(step, 12);
            timersRef.current.push(continueTimer);
          }
        }, HALF_FLIP_MS * 2 + 8);

        timersRef.current.push(settleTimer);
      };

      if (currentChar !== targetChar) {
        step();
      }
    };

    next.split("").forEach((targetChar, i) => {
      if (previous[i] === targetChar) return;
      const startTimer = window.setTimeout(() => {
        animateChar(i, targetChar);
      }, i * CHAR_STAGGER + rowIndex * ROW_DELAY);

      timersRef.current.push(startTimer);
    });

    previousTextRef.current = next;

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [displayText, width, playFlip, rowIndex]);

  const cellWidth = compact ? 28 : 40;
  const cellHeight = compact ? 42 : 60;

  return (
    <div className={`msi-row ${className}`}>
      {cells.map((cell, index) => (
        <FlipChar
          key={`${index}-${bgColor}`}
          value={cell.current}
          nextValue={cell.next}
          isFlipping={cell.isFlipping}
          width={cellWidth}
          height={cellHeight}
          bgColor={bgColor}
          textColor={tokens.colors.bgCard.light}
          borderColor="rgba(27, 69, 132, 0.35)"
        />
      ))}

      <div
        className="msi-row-accent"
        style={{ background: accentColor }}
        aria-hidden="true"
      />
    </div>
  );
}

const MSISplashAltPage = ({
  onEnter,
  soundEnabled = true,
  autoRotate = true,
  rotateInterval = 2600,
}) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!autoRotate) return undefined;

    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % PRODUCTS.length);
    }, rotateInterval);

    return () => window.clearInterval(timer);
  }, [autoRotate, rotateInterval]);

  const active = PRODUCTS[index];

  return (
    <>
      <style>{`
        .msi-splash-root {
          min-height: 100vh;
          background:
            radial-gradient(circle at top, ${MSI_ALT.radial1} 0%, ${MSI_ALT.radial2} 34%, ${MSI_ALT.radial3} 65%, ${MSI_ALT.radial4} 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px;
          color: ${MSI_ALT.textOnGradient};
        }

        .msi-splash-shell {
          width: 100%;
          max-width: 1200px;
          display: grid;
          gap: 28px;
          justify-items: center;
        }

        .msi-brand-block {
          display: grid;
          gap: 12px;
          justify-items: center;
          text-align: center;
        }

        .msi-brand-wordmark {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          font-family: Federo, Georgia, serif;
          font-weight: 400;
          line-height: 1;
          letter-spacing: 0.01em;
          color: ${MSI_ALT.textOnGradient};
        }

        .msi-brand-main {
          font-size: clamp(3.4rem, 10vw, 6.8rem);
          color: ${MSI_ALT.textOnGradient};
        }

        .msi-brand-sup {
          font-size: clamp(1.6rem, 4vw, 3rem);
          color: ${LIGHT_BLUE};
          transform: translateY(0.35rem);
        }

        .msi-brand-subtitle {
          font-family: Federo, Georgia, serif;
          color: ${MSI_ALT.subtitleSoft};
          font-size: clamp(1rem, 2vw, 1.35rem);
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .msi-display-frame {
          width: 100%;
          max-width: 980px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 18px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.28);
          padding: 24px;
          display: grid;
          gap: 18px;
          backdrop-filter: blur(6px);
        }

        .msi-display-title {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: ${MSI_ALT.displayTitle};
          text-align: center;
        }

        .msi-display-rows {
          display: grid;
          gap: 12px;
          justify-content: center;
        }

        .msi-row-wrap {
          display: flex;
          justify-content: center;
        }

        .msi-row {
          position: relative;
          display: inline-flex;
          gap: 1px;
          padding: 10px 12px 10px 10px;
          background: ${tokens.colors.bgCard.light};
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
        }

        .msi-row-accent {
          width: 4px;
          border-radius: 4px;
          margin-left: 8px;
        }

        .msi-flip-char {
          position: relative;
          border: 1px solid;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          perspective: 800px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-weight: 700;
        }

        .msi-flip-top,
        .msi-flip-bottom,
        .msi-flip-top-anim,
        .msi-flip-bottom-anim {
          position: absolute;
          left: 0;
          width: 100%;
          height: 50%;
          overflow: hidden;
          display: flex;
          justify-content: center;
          backface-visibility: hidden;
        }

        .msi-flip-top,
        .msi-flip-top-anim {
          top: 0;
          align-items: flex-end;
          padding-bottom: 1px;
        }

        .msi-flip-bottom,
        .msi-flip-bottom-anim {
          bottom: 0;
          align-items: flex-start;
          padding-top: 1px;
        }

        .msi-flip-top span,
        .msi-flip-bottom span,
        .msi-flip-top-anim span,
        .msi-flip-bottom-anim span {
          font-size: 1.7rem;
          line-height: 1;
          user-select: none;
        }

        .msi-flip-top span,
        .msi-flip-top-anim span {
          transform: translateY(50%);
        }

        .msi-flip-bottom span,
        .msi-flip-bottom-anim span {
          transform: translateY(-50%);
        }

        .msi-flip-crease {
          position: absolute;
          left: 0;
          top: calc(50% - 0.5px);
          width: 100%;
          height: 1px;
          background: rgba(255,255,255,0.33);
          z-index: 5;
          pointer-events: none;
        }

        .msi-flip-top-anim {
          transform-origin: bottom center;
          z-index: 10;
          animation-name: msiFlipTop;
          animation-timing-function: linear;
          animation-fill-mode: forwards;
        }

        .msi-flip-bottom-anim {
          transform-origin: top center;
          transform: rotateX(90deg);
          z-index: 9;
          animation-name: msiFlipBottom;
          animation-timing-function: linear;
          animation-fill-mode: forwards;
        }

        @keyframes msiFlipTop {
          from { transform: rotateX(0deg); }
          to { transform: rotateX(-90deg); }
        }

        @keyframes msiFlipBottom {
          from { transform: rotateX(90deg); }
          to { transform: rotateX(0deg); }
        }

        .msi-descriptor {
          text-align: center;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: ${MSI_ALT.descriptor};
        }

        .msi-controls {
          display: flex;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .msi-button {
          border: 0;
          border-radius: 999px;
          cursor: pointer;
          font-weight: 700;
          font-size: 0.95rem;
          padding: 12px 18px;
          transition: transform 120ms ease, opacity 120ms ease;
        }

        .msi-button:hover {
          transform: translateY(-1px);
        }

        .msi-button-primary {
          background: linear-gradient(180deg, ${shadeHex(BRAND_BLUE, 8)} 0%, ${BRAND_BLUE} 100%);
          color: ${MSI_ALT.textOnGradient};
          box-shadow: 0 10px 24px rgba(27,69,132,0.28);
        }

        .msi-button-secondary {
          background: rgba(255,255,255,0.08);
          color: ${MSI_ALT.textOnGradient};
          border: 1px solid rgba(255,255,255,0.14);
        }

        @media (max-width: 700px) {
          .msi-splash-root {
            padding: 20px;
          }

          .msi-display-frame {
            padding: 16px;
          }

          .msi-flip-top span,
          .msi-flip-bottom span,
          .msi-flip-top-anim span,
          .msi-flip-bottom-anim span {
            font-size: 1.25rem;
          }
        }
      `}</style>

      <div className="msi-splash-root">
        <div className="msi-splash-shell">
          <div className="msi-brand-block">
            <div className="msi-brand-wordmark" aria-label="MSI cubed">
              <span className="msi-brand-main">MSI</span>
              <span className="msi-brand-sup">3</span>
            </div>
            <div className="msi-brand-subtitle">Medical SuperIntelligence</div>
          </div>

          <div className="msi-display-frame">
            <div className="msi-display-title">Platform Suite</div>

            <div className="msi-display-rows">
              <div className="msi-row-wrap">
                <SplitFlapRow
                  text={active.name}
                  width={12}
                  rowIndex={0}
                  bgColor={BRAND_BLUE}
                  accentColor={active.accent}
                  soundEnabled={soundEnabled}
                />
              </div>

              <div className="msi-row-wrap">
                <SplitFlapRow
                  text={active.descriptor}
                  width={22}
                  compact
                  rowIndex={1}
                  bgColor={BRAND_BLUE}
                  accentColor={active.accent}
                  soundEnabled={soundEnabled}
                />
              </div>
            </div>

            <div className="msi-descriptor">
              HealthDesk-native split-flap splash experience
            </div>

            <div className="msi-controls">
              <button
                type="button"
                className="msi-button msi-button-primary"
                onClick={() => {
                  if (typeof onEnter === "function") onEnter();
                }}
              >
                Enter Platform
              </button>

              <button
                type="button"
                className="msi-button msi-button-secondary"
                onClick={() => {
                  setIndex((prev) => (prev + 1) % PRODUCTS.length);
                }}
              >
                Next Product
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MSISplashAltPage;
